import React, { createContext, ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useState } from "react";

import * as microsoftTeams from "@microsoft/teams-js";
import { IUser } from "../types/types";
import * as Constants from "../utils/constants";
import { mobileProviderStyle } from "./ThemeProvider";
import { ApiContext } from "./ApiProvider";
import { registerTokenRenewal } from "../utils/tokenBridge.ts";

interface IContextProviderProps {
    children: ReactNode;
}

interface IAppContext {
    authorized?: boolean;
    setAuthorized: (authorized: boolean) => void;
    expiresIn?: number | undefined;
    setExpiresIn: (expiresIn: number | undefined) => void;
    user?: IUser | undefined;
    setUser: (user: IUser) => void;
    context?: microsoftTeams.app.Context | undefined;
    setContext: (context: microsoftTeams.app.Context) => void;
    token?: string | undefined;
    setToken: (token: string | undefined) => void;
    renewToken: () => Promise<string>;
    providerStyle: any;
    versionNumber?: string | undefined;
}

export const AppContext = createContext<IAppContext>({
    authorized: false,
    setAuthorized: () => {},
    expiresIn: undefined,
    setExpiresIn: () => {},
    user: undefined,
    setUser: () => {},
    context: undefined,
    setContext: () => {},
    token: undefined,
    setToken: () => {},
    renewToken: async () => { return ""; },
    providerStyle: {},
    versionNumber: undefined,
});

export default function ContextProvider({ children }: IContextProviderProps) {
    const { getServerSideVersion, getUser } = useContext(ApiContext);
    const [authorized, setAuthorized] = useState<boolean>(false);
    const [expiresIn, setExpiresIn] = useState<number | undefined>();
    const [user, setUser] = useState<IUser | undefined>();
    const [context, setContext] = useState<microsoftTeams.app.Context | undefined>();
    const [token, setToken] = useState<string | undefined>();
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth >= Constants.MOBILEWIDTH ? false : true);
    const [versionNumber, setVersionNumber] = useState<string | undefined>();
    const providerStyle = isMobile ? mobileProviderStyle : {};

    useEffect(() => {
        window.addEventListener("resize", updateMobileState);

        // Wait for Teams SDK to be initialized before authenticating
        const initializeAndAuthenticate = async () => {
            try {
                // Check if Teams SDK is initialized
                if (!microsoftTeams.app.isInitialized()) {
                    console.log("Teams SDK not initialized yet, waiting...");
                    // Wait a bit and try again
                    setTimeout(initializeAndAuthenticate, 100);
                    return;
                }

                await authenticate();
                getServerSideVersion().then((res) => {
                    console.log("Backend version: " + res.version);
                    console.log("Frontend version: " + process.env.REACT_APP_VERSION);
                    setVersionNumber(process.env.REACT_APP_VERSION + " / " + res.version);
                });
            } catch (error) {
                console.error("Error while authenticating: ", error);
            }
        };

        initializeAndAuthenticate();

        return () => {
            window.removeEventListener("resize", updateMobileState);
        };
    }, []);

    useEffect(() => {
        if (context) {
            // Theme handling removed - using default styling
        }
    }, [context]);

    /**
     * Updates the state of isMobile
     */
    const updateMobileState = () => {
        setIsMobile(window.innerWidth >= Constants.MOBILEWIDTH ? false : true);
    };

    async function checkAuth(token: string) {
        await getUser(token).then((user) => {
            if (user) {
                setUser(user);
            } else {
                console.warn("User not found, let's create one. TODO: Implement user creation");
            }
            return user;
        });
    }

    /**
     * Request a fresh Teams SSO token. Tries silent renewal first so the user
     * is not interrupted after the tab has been idle overnight.
     */
    const requestTeamsAuthToken = async (): Promise<string> => {
        try {
            const silentToken = await microsoftTeams.authentication.getAuthToken({
                resources: [],
                silent: true,
            });
            if (silentToken) {
                return silentToken;
            }
        } catch {
            // Fall through to interactive renewal
        }

        const interactiveToken = await microsoftTeams.authentication.getAuthToken({
            resources: [],
            silent: false,
        });
        if (!interactiveToken) {
            throw new Error("Token is missing");
        }
        return interactiveToken;
    };

    /**
     * Renews the authentication token from Microsoft Teams and updates app state.
     */
    const renewToken = useCallback(async (): Promise<string> => {
        const newToken = await requestTeamsAuthToken();
        setToken(newToken);
        await checkAuth(newToken);
        return newToken;
    }, []);

    useLayoutEffect(() => {
        registerTokenRenewal(renewToken);
        return () => registerTokenRenewal(null);
    }, [renewToken]);

    // Refresh token when the user returns to a backgrounded Teams tab (e.g. left open overnight)
    useEffect(() => {
        const refreshTokenIfVisible = () => {
            if (!document.hidden && microsoftTeams.app.isInitialized()) {
                renewToken().catch((error) => {
                    console.warn("Proactive token refresh failed:", error);
                });
            }
        };

        document.addEventListener("visibilitychange", refreshTokenIfVisible);
        window.addEventListener("focus", refreshTokenIfVisible);

        return () => {
            document.removeEventListener("visibilitychange", refreshTokenIfVisible);
            window.removeEventListener("focus", refreshTokenIfVisible);
        };
    }, [renewToken]);

    /**
     * Authenticates the user and sets the context and token in the state
     *
     * @returns {Promise<void>}
     */
    const authenticate = async (): Promise<void> => {
        microsoftTeams.app.notifySuccess();
        // Get the user context from Teams and set it in the state
        await microsoftTeams.app.getContext().then(async (context) => {
            setContext(context);

            const token = await requestTeamsAuthToken();
            setToken(token);

            await checkAuth(token);
            // microsoftTeams.app.notifySuccess();
        });

        window.addEventListener("onload", updateMobileState);
    };

    return (
        <AppContext.Provider
            value={{
                authorized,
                setAuthorized,
                expiresIn,
                setExpiresIn,
                user,
                setUser,
                context,
                setContext,
                token,
                setToken,
                renewToken,
                providerStyle,
                versionNumber,
            }}
        >
            {children}
        </AppContext.Provider>
    );
}
